import {
  Appointment,
  AppointmentsFilters,
  BookingState,
  Filters,
  SaveForLaterFavorite
} from "core/domain/booking/BookingState";
import { BookingStep } from "core/domain/booking/Enums";
import { Practice } from "core/domain/booking/Practice";
import { BookingStorageService } from "core/services/booking/BookingStorageService";
import { guid } from "core/types";
import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
  toJS
} from "mobx";
import { RouteMap } from "modules/bhb/RouteMap";
import { NavigateOptions, To } from "react-router-dom";
import { ReCaptchaInstance } from "recaptcha-v3";
import { StorageKey } from "store/Storage";

import { DateTime } from "@bps/date-time";

import {
  ConfirmedReservation,
  UnconfirmedReservation
} from "../../core/domain/booking/booking.types";
import {
  AppointmentTypeDto,
  ProviderDto
} from "../../gateway/clients/bhb/bhb.dtos";
import {
  BookingWizardStepHelper,
  BookingWizardStepOptions
} from "./BookingWizardStepHelper.js";

export class BookingStore implements BookingState {
  bookingWizardStepHelper: BookingWizardStepHelper;
  private _suspendSaveToLocalStorage = false;
  private _practice?: Practice;
  private _isUserProfileComplete?: boolean;
  private _hasStartedBooking: boolean = false;
  private _step: BookingStep = BookingStep.SelectPatientType;
  private _isNewPatient?: boolean;
  private _filters: Filters = {};
  private _appointmentFilters: AppointmentsFilters = {};
  private _reservation?: ConfirmedReservation;
  private _unconfirmedReservation?: UnconfirmedReservation;
  private _isAppointmentForUser?: boolean;
  private _dependantId?: string;
  private _appointment?: Appointment;
  private _rescheduleAppointmentId?: string;
  private _patientNotes?: string;
  private _teleHealthContactNumber?: string;
  private _availabilityFromDate: DateTime = DateTime.now().startOf("day");
  private _reCaptchaInstance?: ReCaptchaInstance;
  private _reCaptchaToken: string | null = null;
  private _providerDetailsId: string | undefined;
  private _appointmentTypes?: AppointmentTypeDto[];
  private _providers?: ProviderDto[];
  private _saveForLaterFavorite?: SaveForLaterFavorite;
  private _hasSeenPracticeNotice: boolean = false;

  public constructor() {
    this.bookingWizardStepHelper = new BookingWizardStepHelper();
    makeObservable<
      BookingStore,
      | "_practice"
      | "_isUserProfileComplete"
      | "_step"
      | "_isNewPatient"
      | "_filters"
      | "_reservation"
      | "_unconfirmedReservation"
      | "_isAppointmentForUser"
      | "_dependantId"
      | "_appointment"
      | "_rescheduleAppointmentId"
      | "_patientNotes"
      | "_availabilityFromDate"
      | "_reCaptchaInstance"
      | "_reCaptchaToken"
      | "_providerDetailsId"
      | "_appointmentTypes"
      | "_providers"
      | "_saveForLaterFavorite"
      | "postLogoutRedirectUri"
      | "_appointmentFilters"
      | "_hasSeenPracticeNotice"
      | "_teleHealthContactNumber"
      | "_hasStartedBooking"
    >(this, {
      _practice: observable,
      _isUserProfileComplete: observable,
      _hasStartedBooking: observable,
      _step: observable,
      _isNewPatient: observable,
      _filters: observable,
      _appointmentFilters: observable,
      _reservation: observable,
      _unconfirmedReservation: observable,
      _isAppointmentForUser: observable,
      _dependantId: observable,
      _appointment: observable,
      _rescheduleAppointmentId: observable,
      _patientNotes: observable,
      _teleHealthContactNumber: observable,
      _availabilityFromDate: observable,
      _reCaptchaInstance: observable,
      _reCaptchaToken: observable,
      _providerDetailsId: observable,
      _appointmentTypes: observable,
      _providers: observable,
      _saveForLaterFavorite: observable,
      _hasSeenPracticeNotice: observable,

      // See: https://stackoverflow.com/a/68067250/127497
      availabilityFromDate: computed,
      reCaptchaInstance: computed,
      reCaptchaToken: computed,
      canSelectAppointmentType: computed,
      isUserProfileComplete: computed,
      practiceId: computed,
      practice: computed,
      postLogoutRedirectUri: computed,
      updateBookingState: action.bound,
      tryRestoreCurrentBooking: action.bound,
      resetWizardState: action.bound
    });

    reaction(
      () => ({
        step: this.step,
        practiceId: this.practiceId,
        isNewPatient: this.isNewPatient,
        filters: toJS(this.filters),
        appointmentFilters: toJS(this.appointmentFilters),
        reservation: this.reservation,
        patientNotes: this.patientNotes,
        teleHealthContactNumber: this.teleHealthContactNumber,
        isAppointmentForUser: this.isAppointmentForUser,
        dependantId: this.dependantId,
        appointment: this.appointment,
        unconfirmedReservation: this.unconfirmedReservation,
        rescheduleAppointmentId: this.rescheduleAppointmentId,
        saveForLaterFavorite: this.saveForLaterFavorite
      }),
      state => {
        if (this._suspendSaveToLocalStorage) {
          this._suspendSaveToLocalStorage = false;
        } else {
          BookingStorageService.storeState(state);
        }
      }
    );
  }

  public get practiceId(): guid | undefined {
    if (this._practice?.id) return this._practice?.id;

    const bookingStorage = localStorage.getItem(StorageKey.BHB_WIZARD_MODEL);
    if (bookingStorage) {
      return JSON.parse(bookingStorage).practiceId;
    }
    return undefined;
  }

  public get practice(): Practice {
    return this._practice!;
  }

  public get isUserProfileComplete(): boolean | undefined {
    return this._isUserProfileComplete;
  }
  d;

  public set isUserProfileComplete(next: boolean | undefined) {
    this._isUserProfileComplete = next;
  }

  public get step() {
    return this._step;
  }

  public get isNewPatient() {
    return this._isNewPatient;
  }

  public get hasSeenPracticeNotice() {
    return this._hasSeenPracticeNotice;
  }

  public set hasSeenPracticeNotice(value: boolean) {
    this._hasSeenPracticeNotice = value;
  }

  public get filters() {
    return this._filters;
  }

  public get appointmentFilters() {
    return this._appointmentFilters;
  }

  public get unconfirmedReservation() {
    return this._unconfirmedReservation;
  }

  public get reservation() {
    return this._reservation;
  }

  public get isAppointmentForUser() {
    return this._isAppointmentForUser;
  }

  public get dependantId() {
    return this._dependantId;
  }

  public get patientNotes() {
    return this._patientNotes;
  }

  public get teleHealthContactNumber() {
    return this._teleHealthContactNumber;
  }

  public get appointment() {
    return this._appointment;
  }

  public get rescheduleAppointmentId() {
    return this._rescheduleAppointmentId;
  }

  public get availabilityFromDate() {
    return this._availabilityFromDate;
  }

  public set availabilityFromDate(next: DateTime) {
    this._availabilityFromDate = next;
  }

  public get reCaptchaInstance() {
    return this._reCaptchaInstance;
  }

  public set reCaptchaInstance(next: ReCaptchaInstance | undefined) {
    this._reCaptchaInstance = next;
  }

  public get reCaptchaToken() {
    return this._reCaptchaToken;
  }

  public set reCaptchaToken(next: string | null) {
    this._reCaptchaToken = next;
  }

  public get providerDetailsId() {
    return this._providerDetailsId;
  }

  public get saveForLaterFavorite() {
    return this._saveForLaterFavorite;
  }

  public set saveForLaterFavorite(
    saveForLaterFavorite: SaveForLaterFavorite | undefined
  ) {
    runInAction(() => {
      this._saveForLaterFavorite = saveForLaterFavorite;
    });
  }

  public set providerDetailsId(id: string | undefined) {
    runInAction(() => {
      this._providerDetailsId = id;
    });
  }

  public get canSelectAppointmentType() {
    return (
      this.isNewPatient === false ||
      !this.practice?.bookingSettings?.allowNewPatients
    );
  }

  public get appointmentTypes() {
    return this._appointmentTypes ?? [];
  }

  public get providers() {
    return this._providers ?? [];
  }

  private get hasAppointmentTypeInformation() {
    const appointmentType = this.appointmentTypes.find(
      at => at.id === this.filters.appointmentTypeId
    );
    return appointmentType?.appointmentInformationEnabled ?? false;
  }

  public get hasStartedBooking() {
    return this._hasStartedBooking;
  }

  public set hasStartedBooking(value: boolean) {
    this._hasStartedBooking = value;
  }

  public updateBookingState(delta: Partial<BookingState>) {
    if (!delta) return;

    Object.keys(delta).forEach(key => {
      if (this[key] !== delta[key]) {
        this[`_${key}`] = delta[key];
      }
    });
  }

  public updateFilterState = (delta: Partial<Filters>) => {
    runInAction(() => {
      if (!delta) return;

      Object.keys(delta).forEach(key => {
        if (this._filters[key] !== delta[key]) {
          this._filters[key] = delta[key];
        }
      });
    });
  };

  public updateAppointmentsFilterState = (
    delta: Partial<AppointmentsFilters>
  ) => {
    runInAction(() => {
      if (!delta) return;

      Object.keys(delta).forEach(key => {
        if (this._appointmentFilters[key] !== delta[key]) {
          this._appointmentFilters[key] = delta[key];
        }
      });
    });
  };

  public resetFilterState = () => {
    runInAction(() => {
      this._filters = {
        appointmentTypeId: this.filters.appointmentTypeId,
        appointmentTypeCategoryId: this.filters.appointmentTypeCategoryId
      };
    });
  };

  public resetAppointmentsFilterState = () => {
    runInAction(() => {
      this._appointmentFilters = {};
    });
  };

  public tryRestoreCurrentBooking(
    practice: Practice,
    appointmentTypes?: AppointmentTypeDto[],
    providers?: ProviderDto[]
  ): boolean {
    this._practice = practice;
    this._appointmentTypes = appointmentTypes;
    this._providers = providers;

    const restored = BookingStorageService.restoreState(practice.id);
    if (!restored) return true;

    this._suspendSaveToLocalStorage = true;
    this.updateBookingState(restored);
    return true;
  }

  public resetWizardState() {
    this._isNewPatient = undefined;
    this._filters = {};
    this._reservation = undefined;
    this._unconfirmedReservation = undefined;
    this._isAppointmentForUser = undefined;
    this._dependantId = undefined;
  }

  public get postLogoutRedirectUri() {
    return this.practiceId
      ? RouteMap.getBookingStepPath(this.step, this.practiceId, true)
      : "/";
  }

  public isPatientNotesEnabled = (appointmentTypeId: string) => {
    return (
      this.appointmentTypes.find(x => x.id === appointmentTypeId)
        ?.allowPatientNotes === true
    );
  };

  public isTelehealthConsult = (appointmentTypeId: string) => {
    return (
      this.appointmentTypes.find(x => x.id === appointmentTypeId)
        ?.teleHealthConsult === true
    );
  };

  private bookingWizardStepOptions(): BookingWizardStepOptions {
    return {
      practice: this.practice,
      isNewPatient: this.isNewPatient,
      isAppointmentForUser: this.isAppointmentForUser,
      dependantId: this.dependantId,
      reservation: this.reservation,
      appointment: this.appointment,
      appointmentTypes: this.appointmentTypes,
      providers: this.providers,
      rescheduleAppointmentId: this.rescheduleAppointmentId,
      isUserProfileComplete: this.isUserProfileComplete,
      isPatientNotesEnabled: this.isPatientNotesEnabled(
        this.reservation?.appointmentTypeId ?? ""
      ),
      isTelehealthConsult: this.isTelehealthConsult(
        this.reservation?.appointmentTypeId ?? ""
      ),
      step: this.step,
      hasAppointmentTypeInformation: this.hasAppointmentTypeInformation
    };
  }

  /**
   * Get the logical "current" step based on what information has been filled out in the booking wizard.
   */
  public calculateLogicalStep(requestedStep: BookingStep): BookingStep {
    return this.bookingWizardStepHelper.calculateLogicalStep(
      requestedStep,
      this.bookingWizardStepOptions()
    );
  }

  public getNavigationArgs = (
    step: BookingStep,
    forceReplace = false
  ): [To, NavigateOptions] => {
    if (!this.practiceId) throw Error("locationId is required.");

    const replace = forceReplace || BookingStore.shouldReplaceHistory(step);

    const args = [
      RouteMap.getBookingStepPath(step, this.practiceId, true),
      { replace }
    ];

    return args as [To, NavigateOptions];
  };

  public getPreviousStepNavigationArgs = () =>
    this.getNavigationArgs(
      this.bookingWizardStepHelper.previousStep(this.bookingWizardStepOptions())
    );
  public getNextStepNavigationArgs = () =>
    this.getNavigationArgs(
      this.bookingWizardStepHelper.nextStep(this.bookingWizardStepOptions())
    );

  private static shouldReplaceHistory(step: BookingStep) {
    // When the user goes submits the "Add someone new" form, we need to remove this page from the browser stack.
    // That way, if they click the browser back button, they'll be returned to the "Who is this appointment for" page
    // where their new dependant will now be on the list.
    return step === BookingStep.AddDependant;
  }
}
